import { Spacing, TextVariantWithModifiers } from "@assets/style/appMuiTheme"
import {
  css,
  extractRestFromSpacing,
  GroovyBorderRadiusUnits,
  spacingClass,
  spacingVars,
} from "@assets/style/util/cssUtils"
import { DiscoIcon, DiscoIconKinds, DiscoTooltip, DiscoTooltipProps } from "@disco-ui"
import DiscoText from "@disco-ui/text/DiscoText"
import { Chip, ChipProps } from "@material-ui/core"
import { Skeleton, SkeletonProps } from "@material-ui/lab"
import classNames from "classnames"
import { forwardRef, ReactNode } from "react"
import classes from "./DiscoChip.module.css"

export type DiscoChipColor =
  | "grey"
  | "green"
  | "red"
  | "yellow"
  | "orange"
  | "purple"
  | "blue"
  | "cyan"
  | "primary"

export type DiscoChipProps = Omit<ChipProps, "label" | "color"> &
  Spacing & {
    label: ReactNode
    color?: DiscoChipColor
    testid?: string
    /** Default is 'caption' */
    textVariant?: TextVariantWithModifiers
    leftIcon?: ReactNode | DiscoIconKinds
    rightIcon?: ReactNode | DiscoIconKinds
    customTextColor?: string
    customBackgroundColor?: string
    tooltip?: DiscoTooltipProps
    borderRadius?: GroovyBorderRadiusUnits
  }

const DiscoChip = forwardRef<HTMLDivElement, DiscoChipProps>((props, ref) => {
  const {
    testid,
    label,
    color = "grey",
    className: propsClassName,
    classes: propsClasses,
    textVariant = "body-xs-500",
    leftIcon,
    rightIcon,
    borderRadius = 20,
    customTextColor,
    customBackgroundColor,
    tooltip = {
      disabled: true,
      content: "",
    },
    ...restWithSpacing
  } = props

  return (
    <DiscoTooltip {...tooltip}>
      <Chip
        ref={ref}
        data-testid={`${testid}.chip`}
        data-color={color}
        {...extractRestFromSpacing(restWithSpacing)}
        className={classNames(classes.root, spacingClass(), propsClassName)}
        style={css({
          "--chip-custom-text-color": customTextColor,
          "--chip-custom-background-color": customBackgroundColor,
          "--chip-border-radius": `var(--groovy-border-radius-${borderRadius})`,
          ...spacingVars(props),
        })}
        classes={{
          ...propsClasses,
          label: classNames(classes.label, propsClasses?.label),
          deleteIcon: classNames(
            classes.icon,
            classes.deleteIcon,
            propsClasses?.deleteIcon
          ),
          icon: classNames(classes.icon, propsClasses?.icon),
        }}
        label={
          <div className={classes.labelContainer}>
            {leftIcon && (
              <span className={classes.leftIconContainer}>
                {typeof leftIcon === "string" ? (
                  <DiscoIcon
                    className={classes.leftIcon}
                    icon={leftIcon as DiscoIconKinds}
                    height={16}
                    width={16}
                  />
                ) : (
                  leftIcon
                )}
              </span>
            )}
            {typeof label === "string" || typeof label === "number" ? (
              <DiscoText variant={textVariant} data-testid={testid} setColor={"inherit"}>
                {label}
              </DiscoText>
            ) : (
              label
            )}

            {rightIcon && (
              <span className={classes.rightIconContainer}>
                {typeof rightIcon === "string" ? (
                  <DiscoIcon
                    className={classes.rightIcon}
                    icon={rightIcon as DiscoIconKinds}
                    height={16}
                    width={16}
                  />
                ) : (
                  rightIcon
                )}
              </span>
            )}
          </div>
        }
      />
    </DiscoTooltip>
  )
})

export function getChipProgressColor(value: number): DiscoChipProps["color"] {
  if (value <= 0) return "red"
  if (value >= 100) return "green"
  return "yellow"
}

export const DiscoChipSkeleton = (
  props: Pick<SkeletonProps, "width" | "className"> & Pick<DiscoChipProps, "color">
) => {
  const { color = "blue", className, ...rest } = props
  return (
    <Skeleton
      variant={"rect"}
      className={classNames(classes.root, classes.borderRadiusXl, className)}
      data-color={color}
      {...rest}
    />
  )
}

export default DiscoChip
